import { create } from 'zustand';

const KEY = 'asia_theme';

/** What the user picked. `system` defers to Telegram, which is the environment
 *  the app actually lives in — matching the OS instead would leave the app
 *  light inside a dark Telegram. */
export type ThemePreference = 'light' | 'dark' | 'system';

/** What is actually painted, once `system` has been resolved. */
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  preference: ThemePreference;
  /** Telegram's current scheme, kept so `system` can resolve without asking. */
  telegramScheme: ResolvedTheme;
  resolved: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
  /** Called when Telegram reports a theme change. */
  setTelegramScheme: (scheme: ResolvedTheme) => void;
}

const readPreference = (): ThemePreference => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
  } catch {
    return 'system';
  }
};

const resolve = (p: ThemePreference, tg: ResolvedTheme): ResolvedTheme =>
  p === 'system' ? tg : p;

/** Paint the choice. The attribute is what the token layer in index.css keys
 *  off, so one write re-colours the whole app. */
function apply(theme: ResolvedTheme): void {
  const root = document.documentElement;
  if (theme === 'dark') root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
  // Keeps the browser's own surfaces — scrollbars, form controls, the overscroll
  // gutter — from staying light behind a dark app.
  root.style.colorScheme = theme;
}

export const useTheme = create<ThemeState>((set, get) => {
  const preference = readPreference();
  // Telegram's real scheme arrives a tick later via setTelegramScheme; light is
  // the safer first paint because that is what the design was drawn in.
  const initial = resolve(preference, 'light');
  if (typeof document !== 'undefined') apply(initial);

  return {
    preference,
    telegramScheme: 'light',
    resolved: initial,
    setPreference: (p) => {
      try {
        localStorage.setItem(KEY, p);
      } catch {
        /* a private-mode browser still gets the theme, just not across launches */
      }
      const resolved = resolve(p, get().telegramScheme);
      apply(resolved);
      set({ preference: p, resolved });
    },
    setTelegramScheme: (scheme) => {
      const resolved = resolve(get().preference, scheme);
      if (resolved !== get().resolved) apply(resolved);
      set({ telegramScheme: scheme, resolved });
    },
  };
});
